package com.sddamico.draftmachine.ds.feedback

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.motion.enterOnMount
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.shadowRaised
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

enum class ToastTone { Brass, Verde, Rust, Patina }

private data class ToastSpec(val border: Color, val accent: Color, val icon: String)

private fun ToastTone.spec(): ToastSpec = when (this) {
  ToastTone.Brass -> ToastSpec(DmColor.brass600, DmColor.brass400, "gear-six")
  ToastTone.Verde -> ToastSpec(DmColor.verde600, DmColor.verde400, "check-circle")
  ToastTone.Rust -> ToastSpec(DmColor.rust600, DmColor.rust400, "warning-octagon")
  ToastTone.Patina -> ToastSpec(DmColor.patina600, DmColor.patina400, "info")
}

/**
 * A pneumatic message slip for confirmations and alerts. [tone] sets the accent seam + default icon.
 * Rises in when it appears; pass an `animation` in [modifier] to replace that. Port of
 * `components/feedback/Toast.jsx`.
 */
@Composable
fun Toast(
  title: String? = null,
  message: String? = null,
  tone: ToastTone = ToastTone.Brass,
  icon: String? = null,
  modifier: Modifier = Modifier,
  onClose: (() -> Unit)? = null,
) {
  val spec = tone.spec()
  Div(
    attrs = Modifier.enterOnMount().then(modifier)
      .displayFlex().alignItems(AlignItems.FlexStart).gap(DmSpacing.space3).padding(DmSpacing.space4)
      .backgroundColor(DmColor.surfaceRaised)
      .border(1.px, LineStyle.Solid, spec.border)
      .borderLeft(3.px, LineStyle.Solid, spec.accent)
      .borderRadius(DmEffects.radiusMd)
      .shadowRaised()
      .styleModifier { property("width", "min(380px, 90vw)") }
      .toAttrs { attr("role", "status") }
  ) {
    Icon(
      icon ?: spec.icon, IconWeight.Bold,
      Modifier.fontSize(22.px).color(spec.accent).flex("0 0 auto").margin(top = 1.px)
    )
    Div(attrs = Modifier.flex("1").minWidth(0.px).toAttrs()) {
      if (title != null) {
        Div(attrs = Modifier.roleH3().fontSize(DmType.textBase).margin(bottom = 2.px).toAttrs()) { Text(title) }
      }
      if (message != null) {
        Div(attrs = Modifier.color(DmColor.textSecondary).fontSize(DmType.textSm).toAttrs()) { Text(message) }
      }
    }
    if (onClose != null) {
      DomButton(
        attrs = Modifier.color(DmColor.textMuted).cursor(Cursor.Pointer).padding(2.px)
          .styleModifier { property("background", "none"); property("border", "none") }
          .toAttrs { attr("aria-label", "Dismiss"); onClick { onClose() } }
      ) {
        Icon("x", IconWeight.Bold, Modifier.fontSize(16.px))
      }
    }
  }
}
